import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Header from 'components/Header/Header';
import Loading from 'components/Loading/Loading';
import RedirectToPath from 'components/Route/RedirectToPath';
import { hasWildfirePermissionsSelector, userRolesSelector } from 'helpers/auth0';
import { AUTH0_NAMESPACE } from 'helpers/constants';
import AppGroupOverview from 'pages/AppGroupOverview/AppGroupOverview';
import ApplicationGroups from 'pages/AppGroups/AppGroups';
import Application from 'pages/Application/Application';
import GlobalCampaignsManager from 'pages/GlobalCampaignsManager/GlobalCampaignsManager';
import { getRestrictedPages } from 'pages/Application/constants';
import BoostedOffers from 'pages/BoostedOffers/BoostedOffers';
import BulkMerchantManagement from 'pages/BulkMerchantManagement/BulkMerchantManagement';
import CreateApplication from 'pages/CreateApplication/CreateApplication';
import FeatureDashboard from 'pages/FeatureDashboard/FeatureDashboard';
import Login from 'pages/Login/Login';
import Payment from 'pages/Payment/Payment';
import UserManagement from 'pages/UserManagement/UserManagement';
import { useAppDispatch, useAppSelector } from 'reduxState/hooks';
import { selectUserToken } from 'reduxState/store/user/selectors';
import { setUser, setUserRestrictedRole } from 'reduxState/store/user/slice';
import { getHighestRestrictedUserRole } from 'reduxState/store/user/utils';
import { setAuth0TokenGetter, setAuth0Logout } from 'helpers/tokenRefresh';
import { isInvalidRefreshTokenError, clearAuth0CacheAndLogout } from 'helpers/authErrors';
import ActionRewardsManager from 'pages/ActionRewardsManager/ActionRewardsManager';
import './Main.scss';

const Main = (): JSX.Element => {
  const location = useLocation();
  const auth0 = useAuth0();
  const {
    isAuthenticated,
    isLoading: loadingUser,
    loginWithRedirect,
    user,
    getAccessTokenSilently,
    logout,
    error,
  } = auth0;
  const hasWfPermissions = hasWildfirePermissionsSelector(user);
  const accessToken = useAppSelector(selectUserToken);

  const lastLoginDate = localStorage.getItem('lastLoginDate');
  if (lastLoginDate === '' || Number(lastLoginDate) < new Date(Date.UTC(2022, 12, 9)).getTime()) {
    localStorage.removeItem('commission_columns');
    localStorage.setItem('lastLoginDate', JSON.stringify(Date.now()));
  }
  const dispatch = useAppDispatch();

  // Clear invalid Auth0 cache on mount if refresh token is likely invalid
  useEffect(() => {
    const checkAndClearInvalidCache = async () => {
      // Check if there's Auth0 cache in localStorage
      const auth0Keys = Object.keys(localStorage).filter(key => key.startsWith('@@auth0spajs@@'));

      if (auth0Keys.length > 0 && !isAuthenticated && !loadingUser) {
        // There's cached auth data but user isn't authenticated - likely invalid
        console.warn('Found Auth0 cache but user is not authenticated, clearing stale cache');
        auth0Keys.forEach(key => localStorage.removeItem(key));

        // Force a fresh login
        loginWithRedirect({
          appState: {
            returnTo: location?.pathname,
          },
        });
      }
    };

    // Run this check after a brief delay to let Auth0 SDK initialize
    const timer = setTimeout(checkAndClearInvalidCache, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle Auth0 initialization errors (like invalid refresh token on page load)
  useEffect(() => {
    if (error) {
      console.error('Auth0 initialization error:', error);

      // Check if this is an invalid refresh token error during initialization
      if (isInvalidRefreshTokenError(error)) {
        clearAuth0CacheAndLogout(logout);
      }
    }
  }, [error, logout]);

  useEffect(() => {
    if (!isAuthenticated && !loadingUser && !error) {
      loginWithRedirect({
        appState: {
          returnTo: location?.pathname,
        },
      });
    }
  }, [isAuthenticated, loadingUser, error, location.pathname, loginWithRedirect]);

  useEffect(() => {
    if (user) {
      // Set up Auth0 functions for automatic token refresh on 401s (shared across all API calls)
      setAuth0TokenGetter(getAccessTokenSilently);
      setAuth0Logout(logout);

      getAccessTokenSilently()
        .then((token: string) => {
          dispatch(
            setUser({
              token: token,
              user: user,
              email: user.email || 'Not Available',
              roles: user[`${AUTH0_NAMESPACE}/roles`] ?? [],
            }),
          );
        })
        .catch(error => {
          console.error('Failed to get access token on user initialization:', error);
          clearAuth0CacheAndLogout(logout);
        });
      const userRoles = userRolesSelector(user);
      const highestRestrictedRole = getHighestRestrictedUserRole(userRoles);

      if (highestRestrictedRole) {
        dispatch(setUserRestrictedRole({ restrictedRole: highestRestrictedRole }));
      }
    }
  }, [dispatch, getAccessTokenSilently, logout, user]);

  if (loadingUser && !error) {
    return (
      <div className="main">
        <div className="loading-wrapper">
          <Loading />
        </div>
      </div>
    );
  }
  if (error) {
    console.error('Error authenticating user.');
    return (
      <div className="main">
        <h4>Error authenticating user. Please try logging in again.</h4>
      </div>
    );
  }

  if (!isAuthenticated || !accessToken) {
    return <></>;
  }

  const userRoles = userRolesSelector(user);
  const highestRestrictedRole = getHighestRestrictedUserRole(userRoles);

  let restrictedPagesSet = new Set<string>([]);

  if (highestRestrictedRole && !hasWfPermissions) {
    restrictedPagesSet = new Set<string>(getRestrictedPages(highestRestrictedRole));
  }

  const restrictedPage = <div>You do not have access to view this page.</div>;

  return (
    <div className="main">
      <Routes>
        <Route element={<Header />}>
          <Route path="/login" element={<Login />} />
          <Route path="/features" element={<FeatureDashboard />} />
          {hasWfPermissions && [
            <Route key="create-application" path="/create-application" element={<CreateApplication />} />,
            <Route
              key="bulk-merchant-management"
              path="/:adminAppId/app/:appId/bulk-merchant-management"
              element={<BulkMerchantManagement />}
            />,
            <Route key="global-campaigns-manager" path="/global-campaigns-manager" element={<GlobalCampaignsManager />} />,
            <Route
              key="global-campaigns-manager-v1"
              path="/global-campaigns-manager-v1"
              element={<Navigate to="/global-campaigns-manager" replace />}
            />,
            <Route key="action-rewards-manager" path="/action-rewards-manager" element={<ActionRewardsManager />} />,
          ]}
          <Route path="/:adminAppId/boosted-offers" element={<BoostedOffers />} />
          <Route path="/:adminAppId/user-management" element={<UserManagement />} />
          <Route
            path="/:adminAppId/payment"
            element={!restrictedPagesSet.has('payments') ? <Payment /> : restrictedPage}
          />
          <Route path="/:adminAppId/*" element={<AppGroupOverview />} />
          {/** Old routes that are not in use anymore, redirect user to AppGroup page. Leaving in for now for users with old bookmarks */}
          <Route path="/apps/:appId/*" element={<RedirectToPath path="/" />} />
          <Route path="/apps" element={<RedirectToPath path="/" />} />
          {/** End of old routes */}
          <Route path="/" element={<ApplicationGroups />} />
          <Route path="*" element={<RedirectToPath path="/login" />} />
        </Route>
      </Routes>
    </div>
  );
};

export default Main;
